import { useMicdropState } from '@micdrop/react';
import { Micdrop, Speaker } from '@micdrop/web';
import { Play, Square } from 'lucide-react';
import React, { useRef, useState } from 'react';

type SpeakerTestButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export default function SpeakerTestButton(props: SpeakerTestButtonProps) {
  const { isAssistantSpeaking } = useMicdropState();
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);

  const handleClick = async () => {
    clearTimeout(timeoutRef.current);

    if (Speaker.isPlaying) {
      Speaker.stopAudio();
      return;
    }

    setLoading(true);

    const filepathPattern = '/chunk-{i}.wav';
    let i = 1;
    const maxI = 1;

    const playNextChunk = async () => {
      // Fetch audio chunk
      const filepath = filepathPattern.replace('{i}', i.toString());
      const result = await fetch(filepath);
      if (!result.ok) return;

      // Play audio
      const audioBuffer = await result.arrayBuffer();
      Speaker.playAudio(audioBuffer);
      i++;
      setLoading(false);

      // Schedule next chunk
      if (i <= maxI) {
        timeoutRef.current = window.setTimeout(playNextChunk, 100);
      }
    };

    playNextChunk();

    // Mute mic
    const isMuted = Micdrop.isMuted;
    if (!isMuted) {
      Micdrop.mute();
      Speaker.once('StopPlaying', () => {
        Micdrop.unmute();
      });
    }
  };

  const { className, disabled, ...restProps } = props;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading || disabled}
      className={`
        inline-flex items-center px-4 py-1
        border-2 border-blue-500 text-blue-500 rounded-md
        hover:bg-blue-50
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-200
        ${className || ''}
      `}
      {...restProps}
    >
      {isAssistantSpeaking ? (
        <Square size={14} className="mr-2" />
      ) : (
        <Play size={14} className="mr-2" />
      )}
      Test
    </button>
  );
}
